import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import SEO, { generateOrganizationSchema, generateWebsiteSchema, generateSoftwareSchema, generatePersonSchema } from "../components/SEO";
import { 
  ArrowRight, Check, MapPin, Compass, ChartBar, Users, Download, Play, PencilLine,
  Star, Lightning, ChatCircleDots, FilePdf, Clock, Shield, Sparkle, Crown,
  Brain, Heart, CurrencyInr, Calendar, CheckCircle, Gift, Rocket, BookOpen,
  Planet, StarFour, User, Headset, VideoCamera, Phone, Certificate, Handshake,
  GraduationCap, House, Hash, ShoppingCart, YoutubeLogo, CaretRight, CaretDown,
  GridFour, Cube, MagicWand, UserCircle, SignIn, Envelope, Books, ChalkboardTeacher,
  DownloadSimple, List, X
} from "@phosphor-icons/react";
import PricingModal from "../components/PricingModal";
import { InstallAppButton } from "../components/PWAInstallPrompt";
import CoursesCarousels from "../components/CoursesCarousels";

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_51314d4e-1cb2-4f36-bbc7-4fbfbca7013e/artifacts/ln7j2e9p_Elegant%20gold%20OK%20logo%20design%20%281%29.png";
const SAMPLE_GRID_URL = "https://customer-assets.emergentagent.com/job_pinnacle-grid/artifacts/jbmsg6e5_Untitled%20Project-zones%20%285%29.png";

// Tutorial videos configuration (placeholder - URLs to be added later)
const TUTORIAL_VIDEOS = [
  {
    id: 1,
    title: "Professional Map Griding Using FREE Software",
    description: "Complete 1.5 hour training on professional map griding using free software.",
    thumbnail: "https://i.ytimg.com/vi/uQoqXKIo6TQ/hqdefault.jpg",
    videoUrl: "https://www.youtube.com/watch?v=uQoqXKIo6TQ",
    duration: "1:26:08"
  },
  {
    id: 2,
    title: "Free Trial Vastu Software Login & Usage Guide",
    description: "Learn how to login and use the free trial version of our Vastu software.",
    thumbnail: "https://i.ytimg.com/vi/eVEDPXBZinc/hqdefault.jpg",
    videoUrl: "https://www.youtube.com/watch?v=eVEDPXBZinc",
    duration: "7:22"
  },
  {
    id: 3,
    title: "Premium Vastu Report Section Explained",
    description: "Detailed explanation of the Premium Vastu Report section. Learn how to analyze 16 zones, 45 Devtas.",
    thumbnail: "https://i.ytimg.com/vi/rVpPupE931Q/hqdefault.jpg",
    videoUrl: "https://www.youtube.com/watch?v=rVpPupE931Q",
    duration: "10:56"
  },
  {
    id: 4,
    title: "Premium Numerology Report in Map Griding Software",
    description: "Complete tutorial on generating and understanding Premium Numerology Reports.",
    thumbnail: "https://i.ytimg.com/vi/CBW_tWhTG20/hqdefault.jpg",
    videoUrl: "https://www.youtube.com/watch?v=CBW_tWhTG20",
    duration: "5:27"
  },
  {
    id: 5,
    title: "Premium Astro Report in Map Griding Software",
    description: "Learn how to generate comprehensive Premium Astro Reports including Kundli analysis.",
    thumbnail: "https://i.ytimg.com/vi/WCr36Hu7PT4/hqdefault.jpg",
    videoUrl: "https://www.youtube.com/watch?v=WCr36Hu7PT4",
    duration: "4:48"
  },
  {
    id: 6,
    title: "Premium Kundli Section Explained",
    description: "Complete tutorial on the Premium Kundli section. Understand birth chart analysis.",
    thumbnail: "https://i.ytimg.com/vi/THzLvCumSss/hqdefault.jpg",
    videoUrl: "https://www.youtube.com/watch?v=THzLvCumSss",
    duration: "4:28"
  }
];

// Mobile-nav config (mirrors the desktop header links)
const MOBILE_MAIN_NAV = [
  { label: "Home", path: "/" },
  { label: "Reports", path: "/reports" },
  { label: "Go Premium", path: "/go-premium" },
  { label: "Consultation", path: "/consultation" },
  { label: "Courses", path: "/courses" },
  { label: "Remedies", path: "/remedies" },
  { label: "Tutorials", path: "/tutorials" },
  { label: "Contact Us", path: "/support" },
];

const MOBILE_FREE_TOOLS = [
  { label: "Vastu Editor", path: "/editor" },
  { label: "Kundli Generator", path: "/astro-vastu" },
  { label: "Numerology", path: "/numerology" },
  { label: "Mangal Dosha", path: "/tools/mangal-dosha" },
  { label: "Kalsarpa Dosha", path: "/tools/kalsarpa-dosha" },
  { label: "Sade Sati", path: "/tools/sade-sati" },
  { label: "Tithi & Panchang", path: "/tools/tithi" },
  { label: "Nakshatra Finder", path: "/tools/nakshatra-finder" },
  { label: "Muhurat Finder", path: "/tools/muhurat" },
  { label: "Kundli Milan", path: "/tools/kundli-milan" },
  { label: "Baby Name", path: "/tools/baby-name" },
  { label: "Marriage Date Finder", path: "/tools/marriage-dates" },
  { label: "Tarot Reading", path: "/tools/tarot" },
  { label: "Lal Kitab Amrit", path: "/tools/lal-kitab" },
  { label: "Name Audit", path: "/tools/name-audit" },
  { label: "Vehicle / House Number", path: "/tools/vehicle-number" },
  { label: "Rudraksha Suggestion", path: "/tools/rudraksha" },
];


export default function LandingPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isPremiumUser, setIsPremiumUser] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const [shopCategories, setShopCategories] = useState([]);
  const [shopProducts, setShopProducts] = useState([]);
  const dropdownRef = useRef(null);

  // Fetch native shop categories & products
  useEffect(() => {
    (async () => {
      try {
        const [catRes, prodRes] = await Promise.all([
          fetch(`${API}/api/shop/categories`),
          fetch(`${API}/api/shop/products?limit=6&sort=popular`),
        ]);
        const catData = await catRes.json();
        const prodData = await prodRes.json();
        if (catData.success) setShopCategories(catData.categories.filter(c => c.name !== "Other").slice(0, 12));
        if (prodData.success) setShopProducts(prodData.products.filter(p => p.images?.length > 0).slice(0, 6));
      } catch (e) { console.error(e); }
    })();
  }, [API]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Check for logged-in user
  useEffect(() => {
    const userData = localStorage.getItem('vastu_user') || localStorage.getItem('user');
    if (userData) {
      try {
        const parsed = JSON.parse(userData);
        setUser(parsed);
        // Probe premium status to decide whether to show upgrade banner
        if (parsed?.id) {
          fetch(`${API}/api/users/${parsed.id}/premium-status`)
            .then(r => r.json())
            .then(d => setIsPremiumUser(Boolean(d?.is_premium)))
            .catch(() => setIsPremiumUser(false));
        }
      } catch (e) {
        console.error('Failed to parse user data');
      }
    }

    const handleStorageChange = () => {
      const userData = localStorage.getItem('vastu_user') || localStorage.getItem('user');
      if (userData) {
        try {
          setUser(JSON.parse(userData));
        } catch (e) {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    };

    window.addEventListener('userLogin', handleStorageChange);
    window.addEventListener('userLogout', handleStorageChange);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('userLogin', handleStorageChange);
      window.removeEventListener('userLogout', handleStorageChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Scroll to section helper
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFCF5]">
      <SEO path="/" structuredData={[generateOrganizationSchema(), generateWebsiteSchema(), generateSoftwareSchema(), generatePersonSchema()]} />
      {/* ============ MOBILE HEADER (md:hidden) ============ */}
      <header className="md:hidden bg-white/95 backdrop-blur-md shadow-sm sticky top-0 z-50 border-b border-amber-100" data-testid="mobile-header">
        <div className="px-4 py-3 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')} data-testid="mobile-logo">
            <img src={LOGO_URL} alt="Occult King" className="h-9 w-auto" />
            <div>
              <h1 className="text-base font-bold text-[#1E3A5F] leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Occult King
              </h1>
              <p className="text-[9px] text-amber-600 font-medium tracking-wide">Scientific Vastu Solutions</p>
            </div>
          </div>

          {/* Right: primary CTA + hamburger */}
          <div className="flex items-center gap-2">
            {user ? (
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md h-9 px-3 text-xs"
                data-testid="mobile-dashboard-btn"
              >
                <UserCircle size={16} weight="fill" />
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/signup')}
                className="bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md h-9 px-3 text-xs font-semibold"
                data-testid="mobile-signup-btn"
              >
                Sign Up
              </Button>
            )}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="w-10 h-10 flex items-center justify-center rounded-lg border border-amber-200 text-[#1E3A5F] hover:bg-amber-50 transition-colors"
              aria-label="Open menu"
              data-testid="mobile-menu-toggle"
            >
              <List size={24} weight="bold" />
            </button>
          </div>
        </div>
      </header>

      {/* ============ MOBILE MENU DRAWER ============ */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[100]" data-testid="mobile-menu-overlay">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => { setMobileMenuOpen(false); setMobileToolsOpen(false); }}
          />
          {/* Panel */}
          <div className="absolute top-0 right-0 h-full w-[82%] max-w-sm bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200" data-testid="mobile-menu-panel">
            {/* Panel header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-amber-100">
              <div className="flex items-center gap-2">
                <img src={LOGO_URL} alt="Occult King" className="h-8 w-auto" />
                <span className="font-bold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>Menu</span>
              </div>
              <button
                onClick={() => { setMobileMenuOpen(false); setMobileToolsOpen(false); }}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-amber-50 text-zinc-600"
                aria-label="Close menu"
                data-testid="mobile-menu-close"
              >
                <X size={22} weight="bold" />
              </button>
            </div>

            {/* Auth row */}
            <div className="px-4 py-3 border-b border-amber-50">
              {user ? (
                <Button
                  onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold"
                  data-testid="mobile-menu-dashboard-btn"
                >
                  <UserCircle size={18} weight="fill" className="mr-2" /> My Dashboard
                </Button>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                    className="border-2 border-amber-500 text-amber-600 font-semibold"
                    data-testid="mobile-menu-login-btn"
                  >
                    <SignIn size={16} className="mr-1" /> Login
                  </Button>
                  <Button
                    onClick={() => { navigate('/signup'); setMobileMenuOpen(false); }}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold"
                    data-testid="mobile-menu-signup-btn"
                  >
                    Sign Up
                  </Button>
                </div>
              )}
            </div>

            {/* Scrollable nav links */}
            <nav className="flex-1 overflow-y-auto py-2" data-testid="mobile-menu-nav">
              <button
                onClick={() => { navigate('/'); setMobileMenuOpen(false); }}
                className="w-full px-4 py-3 text-left text-[15px] font-medium text-zinc-700 hover:bg-amber-50 flex items-center gap-3"
                data-testid="mobile-nav-home"
              >
                <House size={18} className="text-amber-600" weight="fill" /> Home
              </button>

              {/* Free Tools collapsible */}
              <button
                onClick={() => setMobileToolsOpen(!mobileToolsOpen)}
                className="w-full px-4 py-3 text-left text-[15px] font-medium text-zinc-700 hover:bg-amber-50 flex items-center justify-between"
                data-testid="mobile-nav-tools-toggle"
              >
                <span className="flex items-center gap-3"><MagicWand size={18} className="text-amber-600" weight="fill" /> Free Tools</span>
                <CaretDown size={16} weight="bold" className={`transition-transform ${mobileToolsOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileToolsOpen && (
                <div className="bg-amber-50/50" data-testid="mobile-tools-list">
                  {MOBILE_FREE_TOOLS.map((t) => (
                    <button
                      key={t.path}
                      onClick={() => { navigate(t.path); setMobileMenuOpen(false); setMobileToolsOpen(false); }}
                      className="w-full pl-12 pr-4 py-2.5 text-left text-sm text-zinc-600 hover:bg-amber-100/60 flex items-center gap-2"
                    >
                      <CaretRight size={12} weight="bold" className="text-amber-500" /> {t.label}
                    </button>
                  ))}
                </div>
              )}

              {MOBILE_MAIN_NAV.filter(n => n.path !== '/').map((n) => (
                <button
                  key={n.path}
                  onClick={() => { navigate(n.path); setMobileMenuOpen(false); }}
                  className="w-full px-4 py-3 text-left text-[15px] font-medium text-zinc-700 hover:bg-amber-50 flex items-center gap-3"
                  data-testid={`mobile-nav-${n.path.replace(/\//g, '') || 'home'}`}
                >
                  {n.label === 'Go Premium' && <Crown size={18} className="text-amber-600" weight="fill" />}
                  {n.label === 'Reports' && <FilePdf size={18} className="text-amber-600" weight="fill" />}
                  {n.label === 'Courses' && <GraduationCap size={18} className="text-amber-600" />}
                  {n.label === 'Remedies' && <ShoppingCart size={18} className="text-amber-600" />}
                  {!['Go Premium','Reports','Courses','Remedies'].includes(n.label) && <CaretRight size={16} className="text-amber-600" weight="bold" />}
                  {n.label}
                </button>
              ))}

              <div className="px-4 py-3">
                <InstallAppButton />
              </div>
            </nav>
          </div>
        </div>
      )}

      {/* ============ TWO-LINE HEADER ============ */}
      <header className="hidden md:block bg-white/95 backdrop-blur-md shadow-sm sticky top-0 z-50 border-b border-amber-100">
        {/* Top Row - Logo + Auth Buttons */}
        <div className="max-w-7xl mx-auto px-4 py-3 border-b border-amber-50">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <img src={LOGO_URL} alt="Occult King" className="h-12 w-auto" />
              <div>
                <h1 className="text-xl font-bold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Occult King
                </h1>
                <p className="text-[10px] text-amber-600 font-medium tracking-wide">Scientific Vastu Solutions</p>
              </div>
            </div>

            {/* Auth Buttons - Top Right */}
            <div className="flex items-center gap-3">
              <InstallAppButton />
              {user ? (
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-md flex items-center gap-2 px-6"
                  data-testid="header-dashboard-btn"
                >
                  <UserCircle size={18} weight="fill" /> My Dashboard
                </Button>
              ) : (
                <>
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/login')}
                    className="border-2 border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white font-semibold px-6"
                    data-testid="header-login-btn"
                  >
                    <SignIn size={18} className="mr-2" /> Login
                  </Button>
                  <Button 
                    onClick={() => navigate('/signup')}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-amber-500/25 font-semibold px-6"
                    data-testid="header-signup-btn"
                  >
                    Create Free Account
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Row - Navigation Tabs */}
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex items-center justify-center gap-1 py-2" ref={dropdownRef}>
            {/* 1. Home */}
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 rounded-lg text-sm font-medium text-zinc-600 hover:text-amber-600 hover:bg-amber-50 transition-colors"
            >
              Home
            </button>

            {/* 2. Free Tools - Dropdown */}
            <div className="relative">
              <button
                onClick={() => setActiveDropdown(activeDropdown === 'tools' ? null : 'tools')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${activeDropdown === 'tools' ? 'text-amber-600 bg-amber-50' : 'text-zinc-600 hover:text-amber-600 hover:bg-amber-50'}`}
              >
                Free Tools <CaretDown size={14} weight="bold" className={`transition-transform ${activeDropdown === 'tools' ? 'rotate-180' : ''}`} />
              </button>
              {activeDropdown === 'tools' && (
                <div className="absolute top-full left-0 mt-1 w-72 bg-white rounded-xl shadow-xl border border-amber-100 py-2 z-50 max-h-[80vh] overflow-y-auto overscroll-contain" data-testid="free-tools-dropdown">
                  <p className="px-4 pb-1 pt-1 text-[10px] font-bold uppercase tracking-wider text-zinc-400">Premium Tools</p>
                  <button onClick={() => { navigate('/editor'); setActiveDropdown(null); }} className="w-full px-4 py-3 text-left hover:bg-amber-50 flex items-center gap-3 transition-colors">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center"><MapPin size={18} className="text-blue-600" weight="fill" /></div>
                    <div><p className="font-medium text-zinc-900">Vastu Editor</p><p className="text-xs text-zinc-500">16 Zones & 45 Devtas</p></div>
                  </button>
                  <button onClick={() => { navigate('/astro-vastu'); setActiveDropdown(null); }} className="w-full px-4 py-3 text-left hover:bg-amber-50 flex items-center gap-3 transition-colors">
                    <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center"><Planet size={18} className="text-purple-600" weight="fill" /></div>
                    <div><p className="font-medium text-zinc-900">Kundli Generator</p><p className="text-xs text-zinc-500">Free Birth Chart</p></div>
                  </button>
                  <button onClick={() => { navigate('/numerology'); setActiveDropdown(null); }} className="w-full px-4 py-3 text-left hover:bg-amber-50 flex items-center gap-3 transition-colors">
                    <div className="w-8 h-8 rounded-lg bg-pink-100 flex items-center justify-center"><Hash size={18} className="text-pink-600" weight="fill" /></div>
                    <div><p className="font-medium text-zinc-900">Numerology</p><p className="text-xs text-zinc-500">Lo Shu Grid & Name Analysis</p></div>
                  </button>
                  {/* New free occult tools */}
                  <div className="border-t border-zinc-100 my-1"></div>
                  <button onClick={() => { navigate('/tools/mangal-dosha'); setActiveDropdown(null); }} className="w-full px-4 py-2.5 text-left hover:bg-amber-50 flex items-center gap-3 transition-colors" data-testid="nav-tool-mangal">
                    <div className="w-7 h-7 rounded-lg bg-rose-100 flex items-center justify-center text-rose-700 font-bold text-xs">♂</div>
                    <div><p className="font-medium text-zinc-900 text-sm">Mangal Dosha</p><p className="text-[11px] text-zinc-500">Manglik Calculator</p></div>
                  </button>
                  <button onClick={() => { navigate('/tools/kalsarpa-dosha'); setActiveDropdown(null); }} className="w-full px-4 py-2.5 text-left hover:bg-amber-50 flex items-center gap-3 transition-colors" data-testid="nav-tool-kalsarpa">
                    <div className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center text-purple-700 font-bold text-xs">☊</div>
                    <div><p className="font-medium text-zinc-900 text-sm">Kalsarpa Dosha</p><p className="text-[11px] text-zinc-500">Yoga Checker</p></div>
                  </button>
                  <button onClick={() => { navigate('/tools/sade-sati'); setActiveDropdown(null); }} className="w-full px-4 py-2.5 text-left hover:bg-amber-50 flex items-center gap-3 transition-colors" data-testid="nav-tool-sadesati">
                    <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-xs">♄</div>
                    <div><p className="font-medium text-zinc-900 text-sm">Sade Sati</p><p className="text-[11px] text-zinc-500">Saturn 7.5-Year Cycle</p></div>
                  </button>
                  <button onClick={() => { navigate('/tools/tithi'); setActiveDropdown(null); }} className="w-full px-4 py-2.5 text-left hover:bg-amber-50 flex items-center gap-3 transition-colors" data-testid="nav-tool-tithi">
                    <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-xs">☽</div>
                    <div><p className="font-medium text-zinc-900 text-sm">Tithi & Panchang</p><p className="text-[11px] text-zinc-500">Any date lookup</p></div>
                  </button>
                  <button onClick={() => { navigate('/tools/nakshatra-finder'); setActiveDropdown(null); }} className="w-full px-4 py-2.5 text-left hover:bg-amber-50 flex items-center gap-3 transition-colors" data-testid="nav-tool-nakshatra">
                    <div className="w-7 h-7 rounded-lg bg-violet-100 flex items-center justify-center text-violet-700 font-bold text-xs">★</div>
                    <div><p className="font-medium text-zinc-900 text-sm">Nakshatra Finder</p><p className="text-[11px] text-zinc-500">Birth Star Lookup</p></div>
                  </button>
                  <button onClick={() => { navigate('/tools/muhurat'); setActiveDropdown(null); }} className="w-full px-4 py-2.5 text-left hover:bg-amber-50 flex items-center gap-3 transition-colors" data-testid="nav-tool-muhurat">
                    <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-xs">⏰</div>
                    <div><p className="font-medium text-zinc-900 text-sm">Muhurat Finder</p><p className="text-[11px] text-zinc-500">Auspicious Times</p></div>
                  </button>
                  <button onClick={() => { navigate('/tools/kundli-milan'); setActiveDropdown(null); }} className="w-full px-4 py-2.5 text-left hover:bg-amber-50 flex items-center gap-3 transition-colors" data-testid="nav-tool-milan">
                    <div className="w-7 h-7 rounded-lg bg-rose-100 flex items-center justify-center text-rose-700 font-bold text-xs">♥</div>
                    <div><p className="font-medium text-zinc-900 text-sm">Kundli Milan</p><p className="text-[11px] text-zinc-500">36-Guna Matching</p></div>
                  </button>
                  <button onClick={() => { navigate('/tools/baby-name'); setActiveDropdown(null); }} className="w-full px-4 py-2.5 text-left hover:bg-amber-50 flex items-center gap-3 transition-colors" data-testid="nav-tool-babyname">
                    <div className="w-7 h-7 rounded-lg bg-pink-100 flex items-center justify-center text-pink-700 font-bold text-xs">👶</div>
                    <div><p className="font-medium text-zinc-900 text-sm">Baby Name</p><p className="text-[11px] text-zinc-500">By Birth Nakshatra</p></div>
                  </button>
                  <button onClick={() => { navigate('/tools/marriage-dates'); setActiveDropdown(null); }} className="w-full px-4 py-2.5 text-left hover:bg-amber-50 flex items-center gap-3 transition-colors" data-testid="nav-tool-marriage-dates">
                    <div className="w-7 h-7 rounded-lg bg-fuchsia-100 flex items-center justify-center text-fuchsia-700 font-bold text-xs">💒</div>
                    <div><p className="font-medium text-zinc-900 text-sm">Marriage Date Finder</p><p className="text-[11px] text-zinc-500">Auspicious wedding dates</p></div>
                  </button>
                  <div className="border-t border-zinc-100 my-1"></div>
                  <button onClick={() => { navigate('/tools/tarot'); setActiveDropdown(null); }} className="w-full px-4 py-2.5 text-left hover:bg-amber-50 flex items-center gap-3 transition-colors" data-testid="nav-tool-tarot">
                    <div className="w-7 h-7 rounded-lg bg-violet-100 flex items-center justify-center text-violet-700 font-bold text-xs">🔮</div>
                    <div><p className="font-medium text-zinc-900 text-sm">Tarot Reading</p><p className="text-[11px] text-zinc-500">Card of Day · 3-Card · Yes/No</p></div>
                  </button>
                  <button onClick={() => { navigate('/tools/lal-kitab'); setActiveDropdown(null); }} className="w-full px-4 py-2.5 text-left hover:bg-amber-50 flex items-center gap-3 transition-colors" data-testid="nav-tool-lalkitab">
                    <div className="w-7 h-7 rounded-lg bg-red-100 flex items-center justify-center text-red-700 font-bold text-xs">📕</div>
                    <div><p className="font-medium text-zinc-900 text-sm">Lal Kitab Amrit</p><p className="text-[11px] text-zinc-500">Planets & remedies</p></div>
                  </button>
                  <button onClick={() => { navigate('/tools/name-audit'); setActiveDropdown(null); }} className="w-full px-4 py-2.5 text-left hover:bg-amber-50 flex items-center gap-3 transition-colors" data-testid="nav-tool-name-audit">
                    <div className="w-7 h-7 rounded-lg bg-orange-100 flex items-center justify-center text-orange-700 font-bold text-xs">📝</div>
                    <div><p className="font-medium text-zinc-900 text-sm">Name Audit</p><p className="text-[11px] text-zinc-500">Chaldean numerology</p></div>
                  </button>
                  <button onClick={() => { navigate('/tools/vehicle-number'); setActiveDropdown(null); }} className="w-full px-4 py-2.5 text-left hover:bg-amber-50 flex items-center gap-3 transition-colors" data-testid="nav-tool-vehicle">
                    <div className="w-7 h-7 rounded-lg bg-sky-100 flex items-center justify-center text-sky-700 font-bold text-xs">🚗</div>
                    <div><p className="font-medium text-zinc-900 text-sm">Vehicle / House Number</p><p className="text-[11px] text-zinc-500">Lucky number check</p></div>
                  </button>
                  <button onClick={() => { navigate('/tools/rudraksha'); setActiveDropdown(null); }} className="w-full px-4 py-2.5 text-left hover:bg-amber-50 flex items-center gap-3 transition-colors" data-testid="nav-tool-rudraksha">
                    <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-xs">📿</div>
                    <div><p className="font-medium text-zinc-900 text-sm">Rudraksha Suggestion</p><p className="text-[11px] text-zinc-500">By birth chart</p></div>
                  </button>
                </div>
              )}
            </div>

            {/* 3. Reports — direct link to public /reports page */}
            <button
              onClick={() => navigate('/reports')}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 text-zinc-600 hover:text-amber-600 hover:bg-amber-50"
              data-testid="nav-reports-link"
            >
              <FilePdf size={14} weight="fill" /> Reports
            </button>

            {/* 4. Go Premium */}
            <button
              onClick={() => navigate('/go-premium')}
              className="px-4 py-2 rounded-lg text-sm font-medium text-amber-600 hover:bg-amber-50 transition-colors flex items-center gap-1"
            >
              <Crown size={16} weight="fill" /> Go Premium
            </button>

            {/* 5. Consultation */}
            <button
              onClick={() => navigate('/consultation')}
              className="px-4 py-2 rounded-lg text-sm font-medium text-zinc-600 hover:text-amber-600 hover:bg-amber-50 transition-colors"
            >
              Consultation
            </button>

            {/* 6. Courses */}
            <button
              onClick={() => navigate('/courses')}
              className="px-4 py-2 rounded-lg text-sm font-medium text-zinc-600 hover:text-amber-600 hover:bg-amber-50 transition-colors flex items-center gap-1"
              data-testid="nav-courses"
            >
              <GraduationCap size={16} /> Courses
            </button>

            {/* 7. Remedies */}
            <button
              onClick={() => navigate('/remedies')}
              className="px-4 py-2 rounded-lg text-sm font-medium text-zinc-600 hover:text-amber-600 hover:bg-amber-50 transition-colors flex items-center gap-1"
            >
              <ShoppingCart size={16} /> Remedies
            </button>

            {/* 8. Tutorials */}
            <button
              onClick={() => navigate('/tutorials')}
              className="px-4 py-2 rounded-lg text-sm font-medium text-zinc-600 hover:text-amber-600 hover:bg-amber-50 transition-colors"
            >
              Tutorials
            </button>

            {/* 9. Contact Us */}
            <button
              onClick={() => navigate('/support')}
              className="px-4 py-2 rounded-lg text-sm font-medium text-zinc-600 hover:text-amber-600 hover:bg-amber-50 transition-colors"
            >
              Contact Us
            </button>
          </nav>
        </div>
      </header>

      {/* ============ PREMIUM UPGRADE BANNER (non-premium users only) ============ */}
      {!isPremiumUser && (
        <section className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-white relative overflow-hidden" data-testid="premium-upgrade-banner">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(255,255,255,0.15),transparent_50%)]"></div>
          <div className="max-w-7xl mx-auto px-4 py-3 md:py-3.5 relative">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center sm:text-left">
              <div className="flex items-center gap-2">
                <Crown size={20} weight="fill" className="text-yellow-100 shrink-0" />
                <p className="text-sm md:text-base font-semibold">
                  <span className="hidden sm:inline">New </span>Premium starts at <span className="font-extrabold">₹527/mo</span>
                  <span className="hidden md:inline"> — 2 report credits + every premium feature</span>
                </p>
              </div>
              <button
                onClick={() => navigate('/premium')}
                className="bg-white text-amber-700 hover:bg-amber-50 font-bold text-xs md:text-sm px-4 py-1.5 rounded-full inline-flex items-center gap-1.5 shadow-sm hover:shadow-md transition-all whitespace-nowrap"
                data-testid="premium-upgrade-banner-cta"
              >
                Compare Plans <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ============ HERO SECTION ============ */}
      <section className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-amber-50/80 via-white to-orange-50/50 -z-10"></div>
        <div className="absolute top-20 right-10 w-72 h-72 bg-amber-200/30 rounded-full blur-3xl -z-10"></div>
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-orange-200/20 rounded-full blur-3xl -z-10"></div>
        
        <div className="max-w-7xl mx-auto px-4 py-10 md:py-24">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            {/* Left Content - 7 cols */}
            <div className="lg:col-span-7">
              {/* Occult King Logo + Brand */}
              <div className="flex items-center gap-4 mb-6">
                <img 
                  src={LOGO_URL} 
                  alt="Occult King" 
                  className="h-16 md:h-20 w-auto"
                  data-testid="hero-brand-logo"
                />
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-[#C5A028] to-[#D4AF37] bg-clip-text text-transparent" style={{ fontFamily: 'Playfair Display, serif' }}>Occult King</h2>
                  <p className="text-xs text-zinc-500 font-medium">by Pinnacle Vastu</p>
                  <div className="inline-flex items-center gap-2 bg-amber-100 border border-amber-200 rounded-full px-3 py-1 mt-1">
                    <Sparkle size={14} weight="fill" className="text-amber-600" />
                    <span className="text-amber-800 text-xs font-medium">Trusted by 25,000+ Users</span>
                  </div>
                </div>
              </div>
              
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-zinc-900 mb-4 md:mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Your Complete{" "}
                <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">
                  Vastu, Astrology & Numerology
                </span>{" "}
                Software
              </h1>
              
              <p className="text-base md:text-xl text-zinc-600 mb-6 md:mb-8 leading-relaxed max-w-2xl">
                Upload floor plans, analyze 16 Vastu zones, generate Kundli, get AI-powered Vedic guidance, 
                and create professional PDF reports — all in one platform by Acharya Vikul Bansal.
              </p>
              
              <div className="flex flex-wrap gap-3 mb-8">
                <Button 
                  size="lg"
                  onClick={() => navigate('/editor')}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-base md:text-lg px-6 md:px-8 py-5 md:py-6 rounded-full shadow-lg shadow-amber-500/30 hover:-translate-y-1 transition-all"
                  data-testid="hero-try-now-btn"
                >
                  <Play className="mr-2" weight="fill" /> Try Free - No Login
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => scrollToSection('tutorials')}
                  className="border-2 border-zinc-300 text-zinc-700 hover:border-amber-500 hover:text-amber-600 text-base md:text-lg px-6 md:px-8 py-5 md:py-6 rounded-full"
                >
                  <VideoCamera className="mr-2" /> Watch Tutorials
                </Button>
              </div>

              {/* Install App Button - Mobile Only */}
              <div className="block md:hidden mb-6">
                <Button 
                  size="sm"
                  onClick={() => navigate('/get-app')}
                  className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-full px-5 py-2.5 text-sm"
                  data-testid="hero-install-app-btn"
                >
                  <DownloadSimple className="mr-2" size={16} weight="bold" /> Install App
                </Button>
              </div>

              {/* Tools Quick Access */}
              <div className="grid grid-cols-5 gap-2 md:gap-3 pt-5 border-t border-zinc-200" data-testid="hero-tools-row">
                <div 
                  onClick={() => navigate('/editor')}
                  className="flex flex-col items-center gap-1.5 cursor-pointer group p-2 rounded-xl hover:bg-amber-50 transition-colors"
                >
                  <div className="w-9 h-9 md:w-10 md:h-10 bg-gradient-to-br from-[#EA580C] to-[#F97316] rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <MapPin size={18} className="text-white" weight="fill" />
                  </div>
                  <span className="text-[10px] md:text-xs font-medium text-zinc-700 text-center">Vastu Grid</span>
                </div>
                <div 
                  onClick={() => navigate('/astro-vastu')}
                  className="flex flex-col items-center gap-1.5 cursor-pointer group p-2 rounded-xl hover:bg-amber-50 transition-colors"
                >
                  <div className="w-9 h-9 md:w-10 md:h-10 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Planet size={18} className="text-white" weight="fill" />
                  </div>
                  <span className="text-[10px] md:text-xs font-medium text-zinc-700 text-center">Kundli</span>
                </div>
                <div 
                  onClick={() => navigate('/numerology')}
                  className="flex flex-col items-center gap-1.5 cursor-pointer group p-2 rounded-xl hover:bg-amber-50 transition-colors"
                >
                  <div className="w-9 h-9 md:w-10 md:h-10 bg-gradient-to-br from-[#DB2777] to-[#F472B6] rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Hash size={18} className="text-white" weight="fill" />
                  </div>
                  <span className="text-[10px] md:text-xs font-medium text-zinc-700 text-center">Numerology</span>
                </div>
                <div 
                  onClick={() => navigate('/pinnacle-guru')}
                  className="flex flex-col items-center gap-1.5 cursor-pointer group p-2 rounded-xl hover:bg-amber-50 transition-colors"
                >
                  <div className="w-9 h-9 md:w-10 md:h-10 bg-gradient-to-br from-[#1E3A5F] to-[#3B82F6] rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <ChatCircleDots size={18} className="text-white" weight="fill" />
                  </div>
                  <span className="text-[10px] md:text-xs font-medium text-zinc-700 text-center">AI Guru</span>
                </div>
                <div 
                  onClick={() => navigate('/compass')}
                  className="flex flex-col items-center gap-1.5 cursor-pointer group p-2 rounded-xl hover:bg-amber-50 transition-colors"
                >
                  <div className="w-9 h-9 md:w-10 md:h-10 bg-gradient-to-br from-[#059669] to-[#10B981] rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Compass size={18} className="text-white" weight="fill" />
                  </div>
                  <span className="text-[10px] md:text-xs font-medium text-zinc-700 text-center">Compass</span>
                </div>
              </div>
            </div>
            
            {/* Right Image - 5 cols - hidden on mobile */}
            <div className="hidden lg:block lg:col-span-5 relative">
              <div className="bg-white rounded-3xl p-4 shadow-2xl shadow-amber-900/10 border border-amber-100">
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-3">
                  <img 
                    src={SAMPLE_GRID_URL} 
                    alt="Vastu Grid Analysis" 
                    className="w-full rounded-xl"
                  />
                </div>
                {/* Floating Badge */}
                <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-3 shadow-lg border border-amber-100">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                      <CheckCircle size={20} className="text-white" weight="fill" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-zinc-900">AI-Powered</p>
                      <p className="text-xs text-zinc-500">Instant Analysis</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ WHAT WE OFFER - Overview Section ============ */}
      <section className="py-16 bg-white border-y border-amber-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-amber-600 font-semibold text-sm tracking-widest uppercase mb-2">Complete Platform</p>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Everything You Need for Vastu, Astrology & Numerology
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { icon: <MapPin size={28} weight="fill" />, title: "Vastu Analysis", desc: "16 Zones + 45 Devtas", color: "from-blue-500 to-blue-600" },
              { icon: <Planet size={28} weight="fill" />, title: "Birth Chart", desc: "Free Kundli", color: "from-purple-500 to-purple-600" },
              { icon: <Hash size={28} weight="fill" />, title: "Numerology", desc: "Lo Shu Grid", color: "from-pink-500 to-pink-600" },
              { icon: <ChatCircleDots size={28} weight="fill" />, title: "AI Guru", desc: "5000 Q/month", color: "from-orange-500 to-orange-600" },
              { icon: <FilePdf size={28} weight="fill" />, title: "PDF Reports", desc: "Detailed Analysis", color: "from-emerald-500 to-emerald-600" },
              { icon: <Users size={28} weight="fill" />, title: "Occult Manager", desc: "For Consultants", color: "from-indigo-500 to-indigo-600" },
            ].map((item, i) => (
              <div key={i} className="bg-zinc-50 hover:bg-white rounded-2xl p-5 text-center hover:shadow-lg hover:shadow-amber-900/5 transition-all cursor-pointer border border-transparent hover:border-amber-100 group">
                <div className={`w-14 h-14 mx-auto rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform`}>
                  {item.icon}
                </div>
                <h4 className="font-semibold text-zinc-900 text-sm">{item.title}</h4>
                <p className="text-xs text-zinc-500 mt-1">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ MAHAKUNDLI — Premium Personalised Report ============ */}
      <section className="py-10 md:py-12 bg-gradient-to-br from-[#1F1B13] via-[#2A2014] to-[#1F1B13] text-white relative overflow-hidden" data-testid="mahakundli-section">
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #A88838 0%, transparent 50%), radial-gradient(circle at 80% 70%, #A88838 0%, transparent 50%)" }} />
        <div className="max-w-6xl mx-auto px-4 relative grid md:grid-cols-2 gap-6 md:gap-8 items-center">
          <div>
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 text-[10px] font-bold tracking-wider uppercase mb-3">
              <Crown size={11} weight="fill" /> Premium Report
            </span>
            <h2 className="text-2xl md:text-3xl font-bold mb-3 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Mahakundli — 50+ page Personalised Vedic Report
            </h2>
            <p className="text-amber-200/90 text-sm md:text-base mb-4">Acharya Vikul Bansal's research engine writes the entire report for YOUR exact chart — not a template. 16 sections, 5 languages, classical Parashari + KP.</p>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 mb-5 text-xs md:text-sm">
              <p className="inline-flex items-center gap-1.5"><span className="text-amber-400">✓</span> Lifetime Mahadasha</p>
              <p className="inline-flex items-center gap-1.5"><span className="text-amber-400">✓</span> House-by-house</p>
              <p className="inline-flex items-center gap-1.5"><span className="text-amber-400">✓</span> Gems, mantras, remedies</p>
              <p className="inline-flex items-center gap-1.5"><span className="text-amber-400">✓</span> 12-month forecast</p>
            </div>
            <button onClick={() => navigate('/mahakundli')} className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-5 py-2.5 rounded-full font-bold text-sm transition" data-testid="home-mahakundli-cta">
              Get My Mahakundli — ₹527 →
            </button>
            <p className="text-[11px] text-amber-300/70 mt-2">Premium subscribers: included in your credit pool</p>
          </div>
          <div className="relative hidden md:block">
            <div className="bg-gradient-to-br from-[#FFF6D6] to-[#FFE8A8] text-[#1F1B13] rounded-2xl shadow-2xl p-5 transform md:rotate-2">
              <p className="text-[10px] tracking-widest uppercase text-[#A88838] font-bold mb-1.5">Mahakundli</p>
              <p className="font-bold text-base mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>Personalised Premium Report</p>
              <p className="text-[11px] text-[#5C4626] mb-3">For Acharya Vikul Bansal · 50+ pages</p>
              <div className="space-y-0.5 text-[11px] text-[#5C4626]">
                <p>· Lagna & Personality (5p)</p>
                <p>· Planet-by-planet (9p)</p>
                <p>· Houses 1-12 (12p)</p>
                <p>· Yogas & Dosha (9p)</p>
                <p>· Mahadasha lifetime (10p)</p>
                <p>· Life predictions (8p)</p>
                <p>· Remedies (5p)</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ COURSES CAROUSELS — Live + Recorded ============ */}
      <section className="py-12 bg-white" data-testid="courses-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-2 px-4">
            <p className="text-[#A88838] font-semibold text-sm tracking-widest uppercase mb-2">Learn From The Master</p>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Live Batches & Recorded Courses
            </h2>
            <p className="text-sm text-zinc-500 mt-2 max-w-2xl mx-auto">
              Vastu, Numerology and Astrology — taught by Acharya Vikul Bansal. WhatsApp support · Certification · Lifetime access on recorded courses.
            </p>
          </div>
          <CoursesCarousels />
        </div>
      </section>

      {/* ============ FREE TOOLS SECTION ============ */}
      <section id="tools" className="py-20 bg-gradient-to-b from-[#FFFCF5] to-amber-50/30">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-amber-600 font-semibold text-sm tracking-widest uppercase mb-2">Get Started Free</p>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Free Occult King Tools
            </h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">No login required. Start using our powerful tools right away.</p>
          </div>
          
          {/* Bento Grid Layout */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 max-w-5xl mx-auto">
            {/* Large Card - Vastu Editor */}
            <div 
              onClick={() => navigate('/editor')}
              className="col-span-2 row-span-2 bg-gradient-to-br from-[#1E3A5F] to-[#3B82F6] text-white rounded-3xl p-6 cursor-pointer hover:shadow-2xl hover:shadow-blue-500/25 transition-all hover:scale-[1.02] group relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <MapPin size={48} className="mb-4" weight="fill" />
              <h3 className="text-2xl font-bold mb-2">Vastu Grid Map</h3>
              <p className="text-white/80 mb-4">Upload floor plan, mark center & boundary, get instant 16-zone analysis with area distribution</p>
              <div className="flex items-center gap-2 text-white/90">
                <span className="bg-white/20 px-3 py-1 rounded-full text-sm">Most Popular</span>
                <ArrowRight size={20} className="ml-auto group-hover:translate-x-2 transition-transform" />
              </div>
            </div>

            {/* Kundli Generator */}
            <div 
              onClick={() => navigate('/astro-vastu')}
              className="col-span-2 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] text-white rounded-2xl p-5 cursor-pointer hover:shadow-xl hover:shadow-purple-500/25 transition-all hover:scale-[1.02]"
            >
              <Planet size={36} className="mb-3" weight="fill" />
              <h4 className="text-lg font-bold">Create My Kundli</h4>
              <p className="text-white/80 text-sm">Free birth chart with Lagna & KP system</p>
            </div>

            {/* Numerology */}
            <div 
              onClick={() => navigate('/numerology')}
              className="col-span-2 bg-gradient-to-br from-[#DB2777] to-[#F472B6] text-white rounded-2xl p-5 cursor-pointer hover:shadow-xl hover:shadow-pink-500/25 transition-all hover:scale-[1.02]"
            >
              <Hash size={36} className="mb-3" weight="fill" />
              <h4 className="text-lg font-bold">Numerology</h4>
              <p className="text-white/80 text-sm">Lo Shu Grid & Name Analysis</p>
            </div>

            {/* Vastu Compass */}
            <div 
              onClick={() => navigate('/compass')}
              className="bg-gradient-to-br from-[#059669] to-[#10B981] text-white rounded-2xl p-5 cursor-pointer hover:shadow-xl hover:shadow-green-500/25 transition-all hover:scale-[1.02]"
            >
              <Compass size={32} className="mb-2" weight="fill" />
              <h4 className="font-bold">Compass</h4>
              <p className="text-white/80 text-xs">16 Zones</p>
            </div>

            {/* Draw Plan */}
            <div 
              onClick={() => navigate('/draw')}
              className="bg-gradient-to-br from-[#EA580C] to-[#F97316] text-white rounded-2xl p-5 cursor-pointer hover:shadow-xl hover:shadow-orange-500/25 transition-all hover:scale-[1.02]"
            >
              <PencilLine size={32} className="mb-2" weight="fill" />
              <h4 className="font-bold">Draw Plan</h4>
              <p className="text-white/80 text-xs">Create Layout</p>
            </div>

            {/* AI Guru - Wide Card */}
            <div 
              onClick={() => navigate('/pinnacle-guru')}
              className="col-span-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-2xl p-5 cursor-pointer hover:shadow-xl hover:shadow-orange-500/25 transition-all hover:scale-[1.02] border-2 border-amber-300"
            >
              <div className="flex items-center gap-3">
                <ChatCircleDots size={36} weight="fill" />
                <div>
                  <h4 className="text-lg font-bold">Occult King Guru AI</h4>
                  <p className="text-white/90 text-sm">5 Free Chats • Ask anything about Vastu</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ PREMIUM SERVICES ============ */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-amber-600 font-semibold text-sm tracking-widest uppercase mb-2">Premium Reports</p>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Detailed Analysis & Remedies
            </h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">Get comprehensive PDF reports with personalized insights and actionable remedies.</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Mahakundli — NEW & FEATURED */}
            <div 
              onClick={() => navigate('/mahakundli')}
              className="bg-gradient-to-br from-[#1F1B13] via-[#3D2C0A] to-[#6B4A1A] text-white rounded-2xl p-6 cursor-pointer hover:shadow-2xl transition-all hover:scale-[1.02] group relative overflow-hidden border-2 border-amber-400/40"
              data-testid="home-reports-mahakundli"
            >
              <div className="absolute top-3 right-3 z-10">
                <span className="bg-gradient-to-r from-amber-400 to-orange-500 text-amber-950 px-2.5 py-1 rounded-full text-[10px] font-extrabold tracking-wider uppercase shadow-lg">★ Most Detailed</span>
              </div>
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_0%,rgba(255,200,80,0.15),transparent_60%)] pointer-events-none"></div>
              <div className="relative">
                <div className="flex items-start justify-between mb-4">
                  <div className="bg-amber-400/20 border border-amber-300/30 p-3 rounded-xl">
                    <Crown size={36} weight="fill" className="text-amber-300" />
                  </div>
                  <div className="flex flex-col items-end gap-0.5 mt-1">
                    <span className="bg-gradient-to-r from-amber-300 to-orange-400 text-amber-950 px-3 py-1 rounded-full text-sm font-bold">₹527</span>
                    <span className="text-[10px] text-amber-200/80">Subscribers ₹257</span>
                  </div>
                </div>
                <h4 className="text-xl font-bold mb-2 text-amber-50">Mahakundli</h4>
                <p className="text-amber-100/80 text-sm mb-4">50+ page personal Vedic report — career, wealth, marriage, health, luck, remedies & 10 more life areas</p>
                <div className="flex items-center text-amber-200/90 text-sm">
                  <CheckCircle size={16} className="mr-1" /> 16 Life Sections
                  <ArrowRight size={18} className="ml-auto group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>

            {/* Premium Vastu Report */}
            <div 
              onClick={() => navigate('/vastu-report')}
              className="bg-gradient-to-br from-[#1E3A5F] via-[#2D5A87] to-[#3B7BC0] text-white rounded-2xl p-6 cursor-pointer hover:shadow-2xl transition-all hover:scale-[1.02] group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="bg-white/20 p-3 rounded-xl">
                  <FilePdf size={36} weight="fill" />
                </div>
                <span className="bg-gradient-to-r from-amber-400 to-orange-400 text-amber-900 px-3 py-1 rounded-full text-sm font-bold">₹527</span>
              </div>
              <h4 className="text-xl font-bold mb-2">Vastu Report</h4>
              <p className="text-white/80 text-sm mb-4">16 zones + 45 Devtas + Room analysis + Remedies</p>
              <div className="flex items-center text-white/90 text-sm">
                <CheckCircle size={16} className="mr-1" /> 50+ Page PDF
                <ArrowRight size={18} className="ml-auto group-hover:translate-x-1 transition-transform" />
              </div>
            </div>

            {/* Astro Report */}
            <div 
              onClick={() => navigate('/astro-report')}
              className="bg-gradient-to-br from-purple-600 via-indigo-600 to-purple-700 text-white rounded-2xl p-6 cursor-pointer hover:shadow-2xl transition-all hover:scale-[1.02] group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="bg-white/20 p-3 rounded-xl">
                  <StarFour size={36} weight="fill" />
                </div>
                <span className="bg-gradient-to-r from-yellow-400 to-amber-400 text-amber-900 px-3 py-1 rounded-full text-sm font-bold">₹527</span>
              </div>
              <h4 className="text-xl font-bold mb-2">Astro-Vastu Report</h4>
              <p className="text-white/80 text-sm mb-4">Kundli + Dasha + Predictions + Remedies</p>
              <div className="flex items-center text-white/90 text-sm">
                <CheckCircle size={16} className="mr-1" /> No Login Needed
                <ArrowRight size={18} className="ml-auto group-hover:translate-x-1 transition-transform" />
              </div>
            </div>

            {/* Numerology Report */}
            <div 
              onClick={() => navigate('/numerology-report')}
              className="bg-gradient-to-br from-[#DB2777] via-[#E11D48] to-[#F43F5E] text-white rounded-2xl p-6 cursor-pointer hover:shadow-2xl transition-all hover:scale-[1.02] group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="bg-white/20 p-3 rounded-xl">
                  <Hash size={36} weight="fill" />
                </div>
                <div className="flex flex-col items-end gap-0.5">
                  <span className="bg-gradient-to-r from-pink-300 to-rose-300 text-pink-900 px-3 py-1 rounded-full text-sm font-bold">₹527</span>
                  <span className="text-[10px] text-white/80">Subscribers ₹257</span>
                </div>
              </div>
              <h4 className="text-xl font-bold mb-2">Numerology Report</h4>
              <p className="text-white/80 text-sm mb-4">Lo Shu + Life Path + 12-Month Forecast</p>
              <div className="flex items-center text-white/90 text-sm">
                <CheckCircle size={16} className="mr-1" /> Crystal Guide
                <ArrowRight size={18} className="ml-auto group-hover:translate-x-1 transition-transform" />
              </div>
            </div>

            {/* Lal Kitab — NEW */}
            <div 
              onClick={() => navigate('/lalkitab')}
              className="bg-gradient-to-br from-[#3B0A0A] via-[#7F1D1D] to-[#B91C1C] text-white rounded-2xl p-6 cursor-pointer hover:shadow-2xl transition-all hover:scale-[1.02] group relative overflow-hidden border-2 border-red-400/40"
              data-testid="home-reports-lalkitab"
            >
              <div className="absolute top-3 right-3 z-10">
                <span className="bg-red-200 text-red-900 px-2.5 py-1 rounded-full text-[10px] font-extrabold tracking-wider uppercase shadow-lg">★ New</span>
              </div>
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_0%,rgba(255,150,80,0.15),transparent_60%)] pointer-events-none"></div>
              <div className="relative">
                <div className="flex items-start justify-between mb-4">
                  <div className="bg-red-300/20 border border-red-200/30 p-3 rounded-xl">
                    <FilePdf size={36} weight="fill" className="text-red-200" />
                  </div>
                  <div className="flex flex-col items-end gap-0.5 mt-1">
                    <span className="bg-gradient-to-r from-red-200 to-orange-300 text-red-950 px-3 py-1 rounded-full text-sm font-bold">₹527</span>
                    <span className="text-[10px] text-red-200/80">Subscribers ₹257</span>
                  </div>
                </div>
                <h4 className="text-xl font-bold mb-2 text-red-50">Lal Kitab Report</h4>
                <p className="text-red-100/80 text-sm mb-4">60+ page Pt. Roop Chand Joshi tradition — 7 Karzas, Buland/Soya/Mandi states, practical totke</p>
                <div className="flex items-center text-red-200/90 text-sm">
                  <CheckCircle size={16} className="mr-1" /> 16 Sections
                  <ArrowRight size={18} className="ml-auto group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>

            {/* Consultation */}
            <div 
              onClick={() => navigate('/consultation')}
              className="bg-gradient-to-br from-emerald-500 via-teal-500 to-emerald-600 text-white rounded-2xl p-6 cursor-pointer hover:shadow-2xl transition-all hover:scale-[1.02] group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="bg-white/20 p-3 rounded-xl">
                  <VideoCamera size={36} weight="fill" />
                </div>
                <span className="bg-white/30 px-3 py-1 rounded-full text-sm font-bold">₹5,000</span>
              </div>
              <h4 className="text-xl font-bold mb-2">Live Consultation</h4>
              <p className="text-white/80 text-sm mb-4">60-min video call with Acharya Vikul Bansal</p>
              <div className="flex items-center text-white/90 text-sm">
                <CheckCircle size={16} className="mr-1" /> Personal Guidance
                <ArrowRight size={18} className="ml-auto group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>

          {/* Go Premium Banner */}
          <div className="mt-12 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-8 text-white text-center">
            <Crown size={48} weight="fill" className="mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-2">Unlock Unlimited Access</h3>
            <p className="text-white/90 mb-6 max-w-xl mx-auto">Get premium reports, unlimited AI chats, Occult Manager for consultants, and more with our subscription plans.</p>
            <Button 
              onClick={() => setShowPricingModal(true)}
              className="bg-white text-amber-600 hover:bg-amber-50 font-bold px-8 py-3 rounded-full"
            >
              View Premium Plans
            </Button>
          </div>
        </div>
      </section>

      {/* ============ TUTORIAL VIDEOS SECTION ============ */}
      <section id="tutorials" className="py-20 bg-gradient-to-b from-zinc-900 to-zinc-950">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-amber-500 font-semibold text-sm tracking-widest uppercase mb-2">Learn & Master</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Video Tutorials
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">Step-by-step guides to help you get the most out of Occult King.</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TUTORIAL_VIDEOS.map((video) => (
              <div 
                key={video.id}
                className="bg-zinc-800/50 rounded-2xl overflow-hidden border border-zinc-700/50 hover:border-amber-500/50 transition-all cursor-pointer group"
                onClick={() => {
                  if (video.videoUrl) {
                    window.open(video.videoUrl, '_blank');
                  } else {
                    toast.info("Video coming soon!");
                  }
                }}
              >
                {/* Thumbnail */}
                <div className="relative aspect-video overflow-hidden">
                  <img 
                    src={video.thumbnail} 
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center">
                      <Play size={32} weight="fill" className="text-white ml-1" />
                    </div>
                  </div>
                  <div className="absolute bottom-2 right-2 bg-black/80 px-2 py-1 rounded text-xs text-white">
                    {video.duration}
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-4">
                  <h4 className="font-semibold text-white mb-1 group-hover:text-amber-400 transition-colors">
                    {video.title}
                  </h4>
                  <p className="text-zinc-400 text-sm">{video.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* YouTube Channel CTA */}
          <div className="mt-10 text-center">
            <Button 
              variant="outline"
              className="border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-white rounded-full px-8"
              onClick={() => window.open("https://www.youtube.com/@AcharyaVikulBansal-55", "_blank")}
            >
              <YoutubeLogo size={20} className="mr-2" weight="fill" /> Subscribe to Our Channel
            </Button>
          </div>
        </div>
      </section>

      {/* ============ SHOP REMEDIES SECTION ============ */}
      <section id="remedies" className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <p className="text-amber-600 font-semibold text-sm tracking-widest uppercase mb-2">Vastu Remedies</p>
              <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Shop Authentic Remedies
              </h2>
              <p className="text-zinc-600 mb-6 text-lg">
                Explore 500+ genuine Vastu remedies, Yantras, Crystals, Gemstones and more — all energized by certified astrologers.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  "Yantras for specific zones",
                  "Certified Gemstones & Rudraksha",
                  "Healing Crystals & Divine Oils", 
                  "Devta Sprays & Treatment Remedies"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                      <Check size={14} className="text-white" weight="bold" />
                    </div>
                    <span className="text-zinc-700">{item}</span>
                  </div>
                ))}
              </div>

              <Button 
                size="lg"
                onClick={() => navigate('/remedies')}
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-full px-8 py-6 shadow-lg shadow-amber-500/30"
              >
                <ShoppingCart size={20} className="mr-2" /> Visit Remedy Shop
              </Button>
            </div>

            {/* Right - Native Categories Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {shopCategories.length > 0 ? (
                shopCategories.slice(0, 6).map((cat, i) => (
                  <div 
                    key={cat.name}
                    className="bg-white rounded-2xl p-3 shadow-lg hover:shadow-xl transition-all cursor-pointer border border-amber-100 hover:border-amber-300 group"
                    onClick={() => navigate('/remedies')}
                  >
                    <div className="aspect-square bg-amber-50 rounded-xl mb-2 overflow-hidden">
                      {cat.image ? (
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-100 to-orange-100">
                          <ShoppingCart size={32} className="text-amber-400" />
                        </div>
                      )}
                    </div>
                    <h4 className="font-semibold text-zinc-900 text-sm group-hover:text-amber-600 transition-colors truncate">{cat.name}</h4>
                    <p className="text-xs text-amber-600">{cat.count} products →</p>
                  </div>
                ))
              ) : (
                [...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl p-4 shadow-lg border border-amber-100 animate-pulse">
                    <div className="aspect-square bg-amber-100 rounded-xl mb-3"></div>
                    <div className="h-4 bg-amber-100 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-amber-50 rounded w-1/2"></div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-amber-600 font-semibold text-sm tracking-widest uppercase mb-2">Success Stories</p>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              What Our Users Say
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: "Rajesh Kumar", role: "Business Owner", text: "The Vastu analysis helped me understand why my office wasn't performing well. After implementing the remedies, we've seen 40% growth!", rating: 5 },
              { name: "Priya Sharma", role: "Homemaker", text: "The Occult King Guru AI is like having a Vastu consultant available 24/7. It answered all my questions about our new home.", rating: 5 },
              { name: "Dr. Anil Verma", role: "Vastu Consultant", text: "The Occult Manager feature has transformed my practice. I can now serve my clients more efficiently with detailed reports.", rating: 5 }
            ].map((testimonial, i) => (
              <div key={i} className="bg-zinc-50 rounded-2xl p-6 border border-zinc-100">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, j) => (
                    <Star key={j} size={18} weight="fill" className="text-amber-500" />
                  ))}
                </div>
                <p className="text-zinc-600 mb-4 italic">"{testimonial.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-zinc-900">{testimonial.name}</p>
                    <p className="text-sm text-zinc-500">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ WHY CHOOSE US ============ */}
      <section className="py-20 bg-gradient-to-b from-white to-amber-50/30">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-amber-600 font-semibold text-sm tracking-widest uppercase mb-2">Why Occult King</p>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Trusted by Thousands
            </h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">Years of expertise combined with modern technology to bring you the most accurate Vastu solutions.</p>
          </div>
          
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            {[
              { number: "25+", label: "Years Experience", icon: <Calendar size={28} weight="fill" /> },
              { number: "10,000+", label: "Happy Clients", icon: <Users size={28} weight="fill" /> },
              { number: "50,000+", label: "Reports Generated", icon: <FilePdf size={28} weight="fill" /> },
              { number: "4.9/5", label: "Client Rating", icon: <Star size={28} weight="fill" /> }
            ].map((stat, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 text-center shadow-lg shadow-amber-900/5 border border-amber-100">
                <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white mb-4">
                  {stat.icon}
                </div>
                <p className="text-3xl md:text-4xl font-bold text-zinc-900 mb-1">{stat.number}</p>
                <p className="text-zinc-500 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Trust Badges */}
          <div className="bg-white rounded-3xl p-8 shadow-xl shadow-amber-900/5 border border-amber-100">
            <h3 className="text-xl font-bold text-center text-zinc-900 mb-8">Why People Choose Us</h3>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { 
                  icon: <GraduationCap size={32} weight="fill" className="text-blue-500" />,
                  title: "Expert Knowledge",
                  description: "Based on teachings of Acharya Vikul Bansal with 25+ years of Vastu expertise and thousands of successful consultations."
                },
                { 
                  icon: <Shield size={32} weight="fill" className="text-emerald-500" />,
                  title: "Scientific Approach",
                  description: "We combine ancient Vastu Shastra wisdom with modern scientific analysis for accurate and practical solutions."
                },
                { 
                  icon: <Rocket size={32} weight="fill" className="text-purple-500" />,
                  title: "AI-Powered Analysis",
                  description: "Our Occult King Guru AI provides instant, accurate guidance available 24/7 - like having a Vastu expert on call."
                },
                { 
                  icon: <Certificate size={32} weight="fill" className="text-amber-500" />,
                  title: "Detailed Reports",
                  description: "Get comprehensive 50+ page PDF reports with zone-wise analysis, remedies, and step-by-step implementation guides."
                },
                { 
                  icon: <Handshake size={32} weight="fill" className="text-pink-500" />,
                  title: "Personalized Remedies",
                  description: "Every recommendation is tailored to your specific floor plan and requirements - no generic one-size-fits-all advice."
                },
                { 
                  icon: <Headset size={32} weight="fill" className="text-indigo-500" />,
                  title: "Dedicated Support",
                  description: "Our support team is always ready to help. Book live consultations for complex cases or quick doubts."
                }
              ].map((item, i) => (
                <div key={i} className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-zinc-100 rounded-xl flex items-center justify-center">
                    {item.icon}
                  </div>
                  <div>
                    <h4 className="font-semibold text-zinc-900 mb-1">{item.title}</h4>
                    <p className="text-zinc-500 text-sm">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ FAQ SECTION ============ */}
      <section className="py-20 bg-zinc-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-amber-600 font-semibold text-sm tracking-widest uppercase mb-2">Have Questions?</p>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Frequently Asked Questions
            </h2>
          </div>
          
          <div className="space-y-4">
            {[
              {
                question: "What is Vastu Shastra and how can it help me?",
                answer: "Vastu Shastra is an ancient Indian science of architecture and design that harmonizes your living or working space with natural forces. By aligning your space according to Vastu principles, you can experience improved health, prosperity, relationships, and overall well-being. Our analysis identifies imbalances and provides practical remedies."
              },
              {
                question: "Do I need to upload my actual floor plan?",
                answer: "Yes, for accurate analysis you should upload your actual floor plan. You can use a builder's plan, a hand-drawn sketch, or even a screenshot from Google Maps. Our editor allows you to mark the center point and boundary, after which the 16-zone analysis is automatically generated."
              },
              {
                question: "Is the Kundli (Birth Chart) generator really free?",
                answer: "Yes! Our Kundli generator is completely free with no login required. You get a detailed birth chart with Lagna chart, planetary positions, Dasha periods, and basic predictions. For more detailed Astro-Vastu analysis with personalized remedies, you can opt for our premium report."
              },
              {
                question: "What's included in the Premium Vastu Report?",
                answer: "Our Premium Vastu Report includes: 16-zone analysis with area distribution, 45 Devtas placement analysis, 32 entrance point evaluation, room-by-room analysis, AI-powered personalized remedies, implementation priority guide, and a comprehensive 50+ page PDF report you can download and share."
              },
              {
                question: "How does the Occult King Guru AI work?",
                answer: "Occult King Guru is our AI assistant trained on authentic Vastu knowledge from Acharya Vikul Bansal's teachings. It can answer your Vastu-related questions, explain concepts, suggest remedies, and guide you through the analysis process. Free users get 5 chats, premium users get up to 5,000 queries per month."
              },
              {
                question: "Can I get a live consultation with an expert?",
                answer: "Yes! We offer live video consultations with Acharya Vikul Bansal and his team. During a 60-minute session, you can discuss your specific concerns, get real-time analysis of your property, and receive personalized guidance. Book through our Consultation page."
              },
              {
                question: "What are the remedies you recommend?",
                answer: "Our remedies are practical and non-invasive. They include placement of Yantras, use of specific colors, arrangement of elements (plants, water features), mirror placements, and sometimes structural suggestions. We never recommend demolition or major construction unless absolutely necessary."
              },
              {
                question: "Is my data secure?",
                answer: "Absolutely. Your floor plans, birth details, and personal information are encrypted and securely stored. We never share your data with third parties. You can delete your account and all associated data at any time from your profile settings."
              }
            ].map((faq, i) => (
              <details key={i} className="group bg-white rounded-xl border border-zinc-200 overflow-hidden">
                <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-amber-50/50 transition-colors">
                  <h4 className="font-semibold text-zinc-900 pr-4">{faq.question}</h4>
                  <CaretRight size={20} className="text-amber-500 group-open:rotate-90 transition-transform flex-shrink-0" weight="bold" />
                </summary>
                <div className="px-5 pb-5 text-zinc-600">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>

          {/* Still have questions CTA */}
          <div className="mt-10 text-center">
            <p className="text-zinc-600 mb-4">Still have questions?</p>
            <Button 
              onClick={() => navigate('/support')}
              variant="outline"
              className="border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white rounded-full px-6"
            >
              <Headset size={18} className="mr-2" /> Contact Support
            </Button>
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="py-20 bg-gradient-to-r from-amber-500 to-orange-500 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10"></div>
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to Transform Your Space?
          </h2>
          <p className="text-amber-100 text-lg mb-8">
            Join thousands of happy users who have harmonized their homes and offices with Occult King.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => navigate('/editor')}
              className="bg-white text-amber-600 hover:bg-amber-50 font-bold px-8 py-6 rounded-full shadow-lg"
            >
              <Play weight="fill" className="mr-2" /> Start Free Analysis
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate('/signup')}
              className="border-2 border-white text-white hover:bg-white hover:text-amber-600 font-bold px-8 py-6 rounded-full"
            >
              Create Free Account
            </Button>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="bg-zinc-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src={LOGO_URL} alt="Occult King" className="h-10 w-auto" />
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>Occult King</h3>
              </div>
              <p className="text-zinc-400 text-sm">Scientific Vastu solutions based on the teachings of Acharya Vikul Bansal.</p>
            </div>
            
            {/* Quick Links */}
            <div>
              <h4 className="text-amber-500 font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-zinc-400 text-sm">
                <li><a href="/editor" className="hover:text-white transition-colors">Vastu Editor</a></li>
                <li><a href="/astro-vastu" className="hover:text-white transition-colors">Kundli Generator</a></li>
                <li><a href="/numerology" className="hover:text-white transition-colors">Numerology</a></li>
                <li><a href="/pinnacle-guru" className="hover:text-white transition-colors">AI Guru</a></li>
              </ul>
            </div>
            
            {/* Reports */}
            <div>
              <h4 className="text-amber-500 font-semibold mb-4">Premium Reports</h4>
              <ul className="space-y-2 text-zinc-400 text-sm">
                <li><a href="/vastu-report" className="hover:text-white transition-colors">Vastu Report - ₹527</a></li>
                <li><a href="/astro-report" className="hover:text-white transition-colors">Astro Report - ₹527</a></li>
                <li><a href="/numerology-report" className="hover:text-white transition-colors">Numerology Report - ₹527</a></li>
              </ul>
            </div>
            
            {/* Contact */}
            <div>
              <h4 className="text-amber-500 font-semibold mb-4">Legal & Support</h4>
              <ul className="space-y-2 text-zinc-400 text-sm">
                <li>
                  <a
                    href="https://wa.me/919093046046"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-green-400 hover:text-green-300 font-medium"
                    data-testid="footer-whatsapp-link"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                      <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-1.607zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.149-.173.198-.297.297-.495.099-.198.05-.372-.025-.521-.074-.149-.669-1.611-.916-2.206-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.521.074-.793.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/>
                    </svg>
                    +91 90930 46046
                  </a>
                </li>
                <li><a href="/terms" className="hover:text-white transition-colors">Terms & Conditions</a></li>
                <li><a href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="/refund-policy" className="hover:text-white transition-colors">Refund Policy</a></li>
                <li><a href="/support" className="hover:text-white transition-colors">Help & Support</a></li>
                <li><a href="mailto:support@occultking.com" className="hover:text-white transition-colors">support@occultking.com</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-zinc-800 mt-12 pt-8 text-center text-zinc-500 text-sm">
            <p>© {new Date().getFullYear()} Occult King by Pinnacle Vastu. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Pricing Modal */}
      {showPricingModal && (
        <PricingModal 
          isOpen={showPricingModal} 
          onClose={() => setShowPricingModal(false)} 
        />
      )}
    </div>
  );
}
